import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { message, Modal, Select, DatePicker } from "antd";
import dayjs from "dayjs";
import Layout from "./common/Layout";
import { get_transactions_list } from "../apis/GetTransactionsList";
import { get_transaction_detail } from "../apis/GetTransactionDetails";
import { confirm_transaction } from "../apis/ConfirmTransaction";
import { update_transaction_status } from "../apis/UpdateTransactionStatus";
import "../css/InComingOrders.scss";
import Loading from "./common/Loading";
import { setAPIAuthorizationToken } from "../apis/API";
import Cookies from "js-cookie";
import { delete_transaction } from "../apis/DeleteTransaction";

const InComingOrders = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // 搜索关键词状态
  const [searchKeyword, setSearchKeyword] = useState("");
  // 实际用于查询的关键词
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");
  // 日期范围状态
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // 实际用于查询的日期范围
  const [activeStartDate, setActiveStartDate] = useState("");
  const [activeEndDate, setActiveEndDate] = useState("");

  // API 数据状态
  const [transactionData, setTransactionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  // 防止重复调用
  const isInitialMount = useRef(true);

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
  }, []);

  // 获取交易列表数据
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const requestParams = {
        search: activeSearchKeyword,
        page: currentPage,
        page_size: pageSize,
      };

      // 添加日期范围参数（如果有值）
      if (activeStartDate) {
        requestParams.start_date = activeStartDate;
      }
      if (activeEndDate) {
        requestParams.end_date = activeEndDate;
      }

      const response = await get_transactions_list(requestParams);

      if (response.data.code === 200) {
        const data = response.data.data;

        // 转换 API 数据格式为组件需要的格式
        const formattedData = (data.list || []).map((item) => ({
          id: item.id, // 保存交易ID，用于获取详情
          user_id: item.user_id, // 保存用户ID，编辑时必需
          transactionNo: item.seller_number || "-",
          collectionAccount: `${item.collection_account || "-"}${
            item.collection_account_name
              ? "/" + item.collection_account_name
              : ""
          }`,
          merchantNo: item.merchant_account_id || "-",
          receivableAmount: `${item.arrival_amount || 0}/${
            item.arrival_currency || ""
          }`,
          depositAmount: `${item.deposit_amount || 0}/${
            item.deposit_currency || ""
          }`,
          fee: `${item.fee || 0}/${item.deposit_currency || ""}`,
          status: item.process_status || "-",
          payerName: item.payer_name || "-",
          transactionDate: item.transaction_time || "-",
          // 详情信息
          platformName: item.platform_name || "-",
          businessType: item.transaction_type_detail || "-",
          storeName: item.shop_name || "-",
          collectionNo: item.collection_account || "-",
          merchantNoDetail: item.seller_number || "-",
          arrivalCurrency: item.arrival_currency || "-",
          arrivalAmount: item.arrival_amount || "0",
          depositCurrency: item.deposit_currency || "-",
          depositAmountDetail: item.deposit_amount || "0",
          feeAmount: item.fee || "0",
          statusDetail: item.process_status || "-",
          remark: item.remark || "-",
          payerNameDetail: item.payer_name || "-",
          transactionTime: item.transaction_time || "-",
          completionTime: item.transaction_complete_time || "-",
          note: item.note || "-",
        }));

        setTransactionData(formattedData);
        // 修复：total 在 pagination 对象中
        setTotal(data.pagination?.total || 0);
      }
    } catch (error) {
      console.error("获取交易列表失败:", error);
      setTransactionData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    activeSearchKeyword,
    activeStartDate,
    activeEndDate,
  ]);

  // 当页码或筛选条件变化时重新获取数据
  useEffect(() => {
    // 防止在 React Strict Mode 下初始挂载时重复调用
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchTransactions();
    } else {
      // 之后的调用都是正常的状态更新触发的
      fetchTransactions();
    }
  }, [fetchTransactions]);

  const handleViewDetail = async (record) => {
    try {
      setDetailLoading(true);
      setShowDetailModal(true);

      // 调用接口获取交易详情
      const response = await get_transaction_detail(record.id);

      if (response.data && response.data.code === 200) {
        const detail = response.data.data;

        // 格式化详情数据 - 保留所有API返回的字段
        const formattedDetail = {
          // 基础信息
          id: detail.id,
          user_id: detail.user_id,
          merchant_account_id: detail.merchant_account_id,

          // 交易编号和账号信息
          transactionNo: detail.actual_number || "-",
          actualNumber: detail.actual_number || "-",
          collectionNo: detail.receiving_account || "-",
          collectionAccount: `${detail.receiving_account || "-"}`,
          merchantNo: detail.merchant_account_number || "-",
          merchantNoDetail: detail.merchant_account_number || "-",
          sellerNumber: detail.seller_number || "-",

          // 平台和店铺信息
          platformName: detail.platform_name || "-",
          storeName: detail.shop_name || "-",

          // 业务类型
          businessType: detail.transaction_type_detail || "-",
          transactionTypeDetail: detail.transaction_type_detail || "-",

          // 金额信息
          amount: detail.amount || "0",
          arrivalAmount: detail.arrival_amount || "0",
          arrivalCurrency: detail.arrival_currency || "-",
          receivableAmount: `${detail.arrival_amount || 0}/${
            detail.arrival_currency || ""
          }`,

          creditedAmount: detail.credited_amount || "0",
          depositAmountDetail: detail.credited_amount || "0",
          depositCurrency: detail.credited_currency || "-",
          depositAmount: `${detail.credited_amount || 0}/${
            detail.credited_currency || ""
          }`,

          feeAmount: detail.fee || "0",
          feeCurrency: detail.fee_currency || "-",
          fee: `${detail.fee || 0}/${detail.fee_currency || ""}`,

          // 余额信息
          balanceBefore: detail.balance_before || "0",
          balanceAfter: detail.balance_after || "0",

          // 状态信息
          status:
            detail.status === 1
              ? "入账成功"
              : detail.status === 0
              ? "待处理"
              : "-",
          statusDetail: detail.process_status || "-",
          processStatus: detail.process_status || "-",
          statusText: detail.status_text || "-",
          completedAt: detail.completed_at || "-",

          // 通知信息
          notificationStatus: detail.notification_status || "-",
          notificationText: detail.notification_text || "-",
          note: detail.notification_text || "-",

          // 付款人信息
          payerName: detail.payer_name || "-",
          payerNameDetail: detail.payer_name || "-",

          // 描述和备注
          description: detail.description || "-",
          remark: detail.remark || "-",

          // 时间信息
          transactionTime: detail.created_at || "-",
          transactionDate: detail.created_at || "-",
          createdAt: detail.created_at || "-",
          creditedAmount_: detail.credited_amount_ || "-",
          completionTime: detail.updated_at || "-",
          updatedAt: detail.updated_at || "-",
        };

        setSelectedRecord(formattedDetail);
      } else {
        console.error("获取交易详情失败:", response.data?.message);
        // 如果API调用失败，使用列表数据作为后备
        setSelectedRecord({});
      }
    } catch (error) {
      console.error("获取交易详情失败:", error);
      // 如果出错，使用列表数据作为后备
      setSelectedRecord({});
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditOrder = async (record) => {
    try {
      setLoading(true);
      // 先获取完整的详情数据，确保所有字段都有值
      const response = await get_transaction_detail(record.id);

      if (response.data && response.data.code === 200) {
        const detail = response.data.data;

        // 格式化详情数据用于编辑
        const editData = {
          // 基础ID信息
          id: detail.id,
          user_id: detail.user_id,
          merchant_account_id: detail.merchant_account_id,

          // 交易信息
          transactionNo: detail.actual_number || "",
          actualNumber: detail.actual_number || "",

          // 平台和店铺信息
          platformName: detail.platform_name || "",
          storeName: detail.shop_name || "",

          // 业务类型
          businessType: detail.transaction_type_detail || "电商",

          // 账号信息
          collectionNo: detail.receiving_account || "",
          collectionAccount: detail.receiving_account || "",
          // 关键：卖家编号字段，用于编辑时传递给 merchant_account_id
          merchantNo: detail.merchant_account_id
            ? String(detail.merchant_account_id)
            : "",
          merchantNoDetail: detail.merchant_account_number || "",
          sellerNumber: detail.seller_number || "",

          // 金额信息
          arrivalAmount: detail.arrival_amount || "0",
          arrivalCurrency: detail.arrival_currency || "EUR",
          depositAmountDetail: detail.credited_amount || "0",
          depositAmount: detail.credited_amount || "0",
          depositCurrency: detail.credited_currency || "EUR",
          feeAmount: detail.fee || "0",
          fee: detail.fee || "0",
          feeCurrency: detail.fee_currency || "EUR",

          // 其他信息
          status: detail.process_status || "",
          statusDetail: detail.process_status || "",
          payerName: detail.payer_name || "",
          payerNameDetail: detail.payer_name || "",
          remark: detail.remark || "",
          note: detail.description || "",

          // 时间信息
          transactionTime: detail.created_at || "",
          transactionDate: detail.created_at || "",
          completionTime: detail.updated_at || "",
        };

        console.log("编辑数据 - 获取详情后:", editData);

        // 导航到编辑页面，传递完整的详情数据
        navigate("/add-incoming-order", { state: { order: editData } });
      } else {
        message.error("获取交易详情失败，无法编辑");
      }
    } catch (error) {
      console.error("获取交易详情失败:", error);
      message.error("获取交易详情失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrder = () => {
    navigate("/add-incoming-order");
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedRecord(null);
  };

  // 所有可能的状态选项
  const statusOptions = [
    { label: "未入账", value: "未入账" },
    { label: "审核中", value: "审核中" },
    { label: "入账成功", value: "入账成功" },
    { label: "退款中", value: "退款中" },
    { label: "退款成功", value: "退款成功" },
  ];

  // 根据当前状态和目标状态，智能确定需要执行的操作
  const getActionForStatusChange = (currentStatus, targetStatus) => {
    // 如果状态相同，不需要操作
    if (currentStatus === targetStatus) {
      return null;
    }

    // 状态转换映射表
    const statusTransitionMap = {
      未入账: {
        审核中: "review",
        入账成功: "confirm",
      },
      审核中: {
        未入账: "cancel_review",
        入账成功: "confirm",
      },
      入账成功: {
        退款中: "refund",
      },
      退款中: {
        入账成功: "cancel_refund",
        退款成功: "confirm_refund",
      },
      退款成功: {
        // 退款成功后通常不能再转换
      },
    };

    return statusTransitionMap[currentStatus]?.[targetStatus] || null;
  };

  // 获取操作名称
  const getActionName = (action) => {
    const actionMap = {
      review: "审核",
      confirm: "确认到账",
      refund: "退款",
      confirm_refund: "确认退款",
      cancel_review: "取消审核",
      cancel_refund: "取消退款",
    };
    return actionMap[action] || "更新状态";
  };

  // 处理下拉框状态变更
  const handleStatusChange = (record, newStatus) => {
    if (!record.id) {
      message.error("交易ID不存在，无法执行操作");
      return;
    }

    const currentStatus = record.status || "未入账";

    // 如果选择的是当前状态，不执行操作
    if (newStatus === currentStatus) {
      return;
    }

    // 获取需要执行的操作
    const action = getActionForStatusChange(currentStatus, newStatus);

    if (!action) {
      message.warning(
        `无法从"${currentStatus}"状态直接转换到"${newStatus}"状态`
      );
      // 重置下拉框到当前状态
      return;
    }

    const actionName = getActionName(action);

    // 确认对话框
    Modal.confirm({
      title: "更新状态",
      content: `确认将交易 ${record.transactionNo} 的状态从"${currentStatus}"更新为"${newStatus}"吗？`,
      okText: "确认",
      cancelText: "取消",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await update_transaction_status(record.id, action);

          if (response.data && response.data.code === 200) {
            message.success(`状态更新成功！`);
            // 刷新列表数据
            await fetchTransactions();
          } else {
            message.error(
              `状态更新失败: ${response.data?.message || "未知错误"}`
            );
          }
        } catch (error) {
          console.error("状态更新失败:", error);
          message.error(
            `状态更新失败: ${
              error.response?.data?.message || error.message || "网络错误"
            }`
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 更新交易状态（通用函数）
  const handleUpdateTransactionStatus = (record, action, actionName) => {
    if (!record.id) {
      message.error("交易ID不存在，无法执行操作");
      return;
    }

    // 确认对话框
    Modal.confirm({
      title: actionName,
      content: `确认${actionName}交易 ${record.transactionNo} 吗？`,
      okText: "确认",
      cancelText: "取消",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await update_transaction_status(record.id, action);

          if (response.data && response.data.code === 200) {
            message.success(`${actionName}成功！`);
            // 刷新列表数据
            await fetchTransactions();
          } else {
            message.error(
              `${actionName}失败: ${response.data?.message || "未知错误"}`
            );
          }
        } catch (error) {
          console.error(`${actionName}失败:`, error);
          message.error(
            `${actionName}失败: ${
              error.response?.data?.message || error.message || "网络错误"
            }`
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 确认交易到账（保留兼容性）
  const handleConfirmTransaction = (record) => {
    handleUpdateTransactionStatus(record, "confirm", "确认到账");
  };

  // 处理交易日期变更
  const handleDateChange = async (record, date) => {
    if (!record.id) {
      message.error("交易ID不存在，无法更新日期");
      return;
    }

    if (!date) {
      message.warning("请选择有效的日期");
      return;
    }

    // 格式化日期为 YYYY-MM-DD HH:mm:ss 格式
    const formattedDate = dayjs(date).format("YYYY-MM-DD HH:mm:ss");
    const originalDate = record.transactionDate;

    // 如果日期没有变化，不执行更新
    if (formattedDate === originalDate) {
      return;
    }

    // 确认对话框
    Modal.confirm({
      title: "更新交易日期",
      content: `确认将交易 ${record.transactionNo} 的日期从"${originalDate}"更新为"${formattedDate}"吗？`,
      okText: "确认",
      cancelText: "取消",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await update_transaction_status(
            record.id,
            "update_date",
            formattedDate
          );

          if (response.data && response.data.code === 200) {
            message.success("交易日期更新成功！");
            // 刷新列表数据
            await fetchTransactions();
          } else {
            message.error(
              `日期更新失败: ${response.data?.message || "未知错误"}`
            );
          }
        } catch (error) {
          console.error("日期更新失败:", error);
          message.error(
            `日期更新失败: ${
              error.response?.data?.message || error.message || "网络错误"
            }`
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 查询按钮处理
  const handleSearch = () => {
    setActiveSearchKeyword(searchKeyword); // 更新实际查询关键词
    setActiveStartDate(startDate); // 更新实际查询开始日期
    setActiveEndDate(endDate); // 更新实际查询结束日期
    setCurrentPage(1); // 重置到第一页
  };

  // 重置按钮处理
  const handleReset = () => {
    setSearchKeyword("");
    setActiveSearchKeyword(""); // 清空实际查询关键词
    setStartDate(""); // 清空开始日期
    setEndDate(""); // 清空结束日期
    setActiveStartDate(""); // 清空实际查询开始日期
    setActiveEndDate(""); // 清空实际查询结束日期
    setCurrentPage(1); // 重置到第一页
  };

  // 删除订单
  const handleDelete = (account) => {
    Modal.confirm({
      title: "确认删除",
      content: `确定要删除订单"${account.transactionNo}"吗？此操作不可恢复。`,
      okText: "确认删除",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        try {
          const response = await delete_transaction(account.id);
          if (response.data && response.data.code === 200) {
            message.success("删除成功");
            fetchTransactions();
          } else {
            message.error(response.data?.message || "删除失败");
          }
        } catch (error) {
          console.error("删除账户失败:", error);
          message.error(
            error.response?.data?.message || "删除失败，请稍后重试"
          );
        }
      },
    });
  };

  return (
    <Layout>
      <div className="incoming-orders-container">
        {/* 顶部标签 */}
        <div className="top-tabs">
          <div className="tab-item active">电商</div>
          <div className="action-buttons-area">
            <button className="btn-add-order" onClick={handleAddOrder}>
              <span className="icon-plus">+</span> 新增订单
            </button>
          </div>
        </div>

        {/* 筛选区域 */}
        <div className="filter-section">
          <div className="search-form">
            <div className="form-row">
              <input
                type="text"
                placeholder="请输入关键词搜索（交易编号、收款账号、卖家编号等）"
                className="form-input"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
              />
              <input
                type="date"
                className="form-input date-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="开始日期"
              />
              <span className="date-separator">至</span>
              <input
                type="date"
                className="form-input date-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="结束日期"
              />
              <button className="btn-primary" onClick={handleSearch}>
                查询
              </button>
              <button className="btn-secondary" onClick={handleReset}>
                重置
              </button>
            </div>
          </div>
        </div>

        {/* 操作按钮区域 */}

        {/* 数据表格 */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>交易编号</th>
                <th>收款账号</th>
                <th>卖家编号</th>
                <th>到账金额/币种</th>
                <th>入账金额/币种</th>
                <th>手续费/币种</th>
                <th>操作</th>
                <th>付款人名称</th>
                <th>交易日期</th>
                <th>管理</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="10"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    <Loading />
                  </td>
                </tr>
              ) : transactionData.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    暂无数据
                  </td>
                </tr>
              ) : (
                transactionData.map((record, index) => (
                  <tr key={index}>
                    <td>{record.transactionNo}</td>
                    <td>{record.collectionAccount}</td>
                    <td>{record.user_id}</td>
                    <td>{record.receivableAmount}</td>
                    <td>{record.depositAmount}</td>
                    <td>{record.fee}</td>
                    <td>
                      <div className="status-select-container">
                        <Select
                          key={`status-${record.id}-${record.status}`}
                          value={record.status || "未入账"}
                          onChange={(value) =>
                            handleStatusChange(record, value)
                          }
                          style={{ width: "100%", minWidth: "120px" }}
                          size="small"
                          className="status-select"
                          data-status={record.status || "未入账"}
                          placeholder="选择状态"
                        >
                          {statusOptions.map((option) => (
                            <Select.Option
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </td>
                    <td>{record.payerName}</td>
                    <td>
                      <div className="date-picker-container">
                        <DatePicker
                          key={`date-${record.id}-${record.transactionDate}`}
                          value={
                            record.transactionDate &&
                            record.transactionDate !== "-" &&
                            dayjs(record.transactionDate).isValid()
                              ? dayjs(record.transactionDate)
                              : null
                          }
                          onChange={(date) => handleDateChange(record, date)}
                          showTime
                          format="YYYY-MM-DD HH:mm:ss"
                          size="small"
                          style={{ width: "100%", minWidth: "180px" }}
                          placeholder="选择日期"
                          allowClear={false}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-detail"
                          onClick={() => handleViewDetail(record)}
                        >
                          详情
                        </button>
                        <button
                          className="btn-edit"
                          onClick={() => handleEditOrder(record)}
                        >
                          编辑
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(record)}
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <div className="pagination-info">
            共 {total} 条{" "}
            <button
              className="page-btn"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              &lt;
            </button>
            <button className="page-btn active">{currentPage}</button>
            {currentPage < Math.ceil(total / pageSize) && (
              <button
                className="page-btn"
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                {currentPage + 1}
              </button>
            )}
            <button
              className="page-btn"
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(Math.ceil(total / pageSize), prev + 1)
                )
              }
              disabled={currentPage >= Math.ceil(total / pageSize)}
            >
              &gt;
            </button>
            <select
              className="page-size-select"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10条/页</option>
              <option value="20">20条/页</option>
              <option value="50">50条/页</option>
            </select>
            <span className="go-to">前往</span>
            <input
              type="number"
              className="page-input"
              min="1"
              max={Math.ceil(total / pageSize)}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= Math.ceil(total / pageSize)) {
                  setCurrentPage(page);
                }
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= Math.ceil(total / pageSize)) {
                    setCurrentPage(page);
                  }
                }
              }}
            />
            <span>页</span>
          </div>
        </div>
      </div>
      <div className="page-footer">
        <p> © 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
      </div>

      {/* 详情弹窗 */}
      {showDetailModal && (
        <div className="modal-overlay1" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>详情</h3>
              <button className="close-btn" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {detailLoading ? (
                <Loading />
              ) : selectedRecord ? (
                <div className="detail-grid">
                  {/* 第一行 - 交易基本信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>交易编号：</label>
                      <span>{selectedRecord.transactionNo}</span>
                    </div>
                    <div className="detail-item">
                      <label>实际编号：</label>
                      <span>{selectedRecord.actualNumber}</span>
                    </div>
                    <div className="detail-item">
                      <label>业务类型：</label>
                      <span>{selectedRecord.businessType}</span>
                    </div>
                  </div>

                  {/* 第二行 - 平台和店铺信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>平台名称：</label>
                      <span>{selectedRecord.platformName}</span>
                    </div>
                    <div className="detail-item">
                      <label>店铺名称：</label>
                      <span>{selectedRecord.storeName}</span>
                    </div>
                    <div className="detail-item">
                      <label>付款人名称：</label>
                      <span>{selectedRecord.payerName}</span>
                    </div>
                  </div>

                  {/* 第三行 - 账号信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>收款账号：</label>
                      <span>{selectedRecord.collectionNo}</span>
                    </div>
                    <div className="detail-item">
                      <label>商户账号编号：</label>
                      <span>{selectedRecord.merchantNo}</span>
                    </div>
                    <div className="detail-item">
                      <label>卖家编号：</label>
                      <span>{selectedRecord.user_id}</span>
                    </div>
                  </div>

                  {/* 第四行 - 金额信息（到账） */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>金额：</label>
                      <span>{selectedRecord.amount}</span>
                    </div>
                    <div className="detail-item">
                      <label>到账金额：</label>
                      <span>{selectedRecord.arrivalAmount}</span>
                    </div>
                    <div className="detail-item">
                      <label>到账币种：</label>
                      <span>{selectedRecord.arrivalCurrency}</span>
                    </div>
                  </div>

                  {/* 第五行 - 金额信息（入账） */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>入账金额：</label>
                      <span>{selectedRecord.creditedAmount}</span>
                    </div>
                    <div className="detail-item">
                      <label>入账币种：</label>
                      <span>{selectedRecord.depositCurrency}</span>
                    </div>
                    <div className="detail-item">
                      <label>手续费：</label>
                      <span>{selectedRecord.feeAmount}</span>
                    </div>
                  </div>

                  {/* 第六行 - 手续费和余额信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>手续费币种：</label>
                      <span>{selectedRecord.feeCurrency}</span>
                    </div>
                    <div className="detail-item">
                      <label>变更前余额：</label>
                      <span>{selectedRecord.balanceBefore}</span>
                    </div>
                    <div className="detail-item">
                      <label>变更后余额：</label>
                      <span>{selectedRecord.balanceAfter}</span>
                    </div>
                  </div>

                  {/* 第七行 - 状态信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>状态：</label>
                      <span>{selectedRecord.statusText}</span>
                    </div>
                    <div className="detail-item">
                      <label>处理状态：</label>
                      <span>{selectedRecord.processStatus}</span>
                    </div>
                    <div className="detail-item">
                      <label>通知状态：</label>
                      <span>{selectedRecord.notificationStatus}</span>
                    </div>
                  </div>

                  {/* 第八行 - 时间信息 */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>创建时间：</label>
                      <span>{selectedRecord.createdAt}</span>
                    </div>
                    <div className="detail-item">
                      <label>更新时间：</label>
                      <span>{selectedRecord.updatedAt}</span>
                    </div>
                  </div>

                  {/* 第九行 - 描述和备注 */}
                  <div className="detail-row">
                    <div className="detail-item ">
                      <label>描述：</label>
                      <span>{selectedRecord.description}</span>
                    </div>
                    <div className="detail-item  ">
                      <label>备注：</label>
                      <span>{selectedRecord.remark}</span>
                    </div>
                    <div className="detail-item">
                      <label>通知文本：</label>
                      <span>{selectedRecord.notificationText}</span>
                    </div>
                  </div>

                  {/* 系统ID（调试用，可选） */}
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>交易ID：</label>
                      <span>{selectedRecord.id}</span>
                    </div>
                    <div className="detail-item">
                      <label>用户ID：</label>
                      <span>{selectedRecord.user_id}</span>
                    </div>
                    <div className="detail-item">
                      <label>商户账户ID：</label>
                      <span>{selectedRecord.merchant_account_id}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  暂无数据
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default InComingOrders;
